import React, { FC, useEffect, useMemo, useState } from "react";
import { t, Trans } from "@helpers/translate";
import { getReadablePeopleValue } from "@components/pages/Booking/sidebar/PeopleInfo";
import { InputIncrement, InputModal } from "@components/ui/Form";

interface InputProps {
  className?: string;
  defaultValue?: {
    adults: number;
    children: number;
    infants: number;
  };
  minPeople?: number;
  maxPeople?: number;
  showErrorView?: boolean;
  onApply: (value: {
    adults: number;
    children: number;
    infants: number;
  }) => void;
  onReset?: () => void;
}

export const InputPeople: FC<InputProps> = ({
  className = "",
  defaultValue,
  onApply,
  onReset,
  showErrorView = false,
  minPeople = 1,
  maxPeople = 6,
}) => {
  const defaultAdults = defaultValue?.adults || 0;
  const defaultChildren = defaultValue?.children || 0;
  const defaultInfants = defaultValue?.infants || 0;

  const [people, setPeople] = useState({
    adults: defaultAdults,
    children: defaultChildren,
    infants: defaultInfants,
  });

  const peopleCount = useMemo(() => {
    return people.adults + people.children + people.infants;
  }, [people]);

  // Ensure there's always at least 1 adult if there are children or infants
  useEffect(() => {
    if ((people.children > 0 || people.infants > 0) && people.adults < 1) {
      setPeople((prev) => ({
        ...prev,
        adults: 1,
      }));
    }
  }, [people.children, people.infants, people.adults]);

  // Handle default value changes
  useEffect(() => {
    setPeople({
      adults: defaultAdults,
      children: defaultChildren,
      infants: defaultInfants,
    });
  }, [defaultValue]);

  const displayValue = peopleCount
    ? getReadablePeopleValue(people.adults, people.children, people.infants)
    : undefined;

  const isDisabled = peopleCount >= minPeople && peopleCount <= maxPeople;
  const incrementDisabled = !(peopleCount < maxPeople);

  return (
    <InputModal
      icon="user"
      showErrorView={showErrorView}
      className={className}
      title={t({ id: "tour.input.people", message: "People" })}
      value={displayValue}
      onApply={isDisabled ? () => people.adults && onApply(people) : undefined}
      onReset={() => onReset && onReset()}
      modalClassName="px-6"
    >
      <div className="my-2">
        <div className="flex justify-between items-center mb-4 md:w-full">
          <div className="mr-4">
            <Trans id="tour.input.adults">Adults</Trans>
          </div>
          <InputIncrement
            value={people.adults}
            onValueChange={(adults) => {
              // Prevent reducing adults below 1 if there are children or infants
              if (adults < 1 && (people.children > 0 || people.infants > 0)) {
                return;
              }
              if (adults < 0) return;
              const updated = {
                ...people,
                adults,
              };
              setPeople(updated);
              onApply(updated);
            }}
            incrementDisabled={incrementDisabled}
            decrementDisabled={people.adults <= 1 || people.adults <= 0}
            min={0}
          />
        </div>
        <div className="flex justify-between items-center mb-4 md:w-full">
          <div className="mr-4">
            <Trans id="tour.input.children">Children</Trans>
            <div className="text-sm text-gray-500">
              <Trans id="tour.input.children.help">6 - 11 years</Trans>
            </div>
          </div>
          <InputIncrement
            value={people.children}
            onValueChange={(children) => {
              const updated = {
                ...people,
                children,
                // Ensure at least one adult if adding children
                adults: people.adults === 0 ? 1 : people.adults,
              };
              setPeople(updated);
              onApply(updated);
            }}
            incrementDisabled={incrementDisabled}
            min={0}
          />
        </div>
        <div className="flex justify-between items-center mb-4 md:w-full">
          <div className="mr-4">
            <Trans id="tour.input.infants">Infants</Trans>
          </div>
          <InputIncrement
            value={people.infants}
            onValueChange={(infants) => {
              const updated = {
                ...people,
                infants,
                // Ensure at least one adult if adding infants
                adults: people.adults === 0 ? 1 : people.adults,
              };
              setPeople(updated);
              onApply(updated);
            }}
            incrementDisabled={incrementDisabled}
            min={0}
          />
        </div>
      </div>
    </InputModal>
  );
};
